import { useState } from "react";

type Question = {
  q: string;
  options: string[];
  answer: number;
};

const questions: Question[] = [
  {
    q: "What is the sacred bloom of the realm?",
    options: ["Rose", "Dahlia", "Tulip", "Orchid"],
    answer: 1,
  },
  {
    q: "What is the currently approved water of Her Highness?",
    options: ["Evian", "Fiji", "Voss", "Smartwater"],
    answer: 2,
  },
  {
    q: "Which water era ended by conscience?",
    options: ["Evian", "Fiji", "Voss", "Aquafina"],
    answer: 1,
  },
  {
    q: "What drink completes the sacred Chick-fil-A order?",
    options: ["Diet Lemonade", "Sweet Tea", "Frosted Coffee", "Diet Coke"],
    answer: 2,
  },
  {
    q: "Which fruit is a medical hazard and shall not cross the threshold?",
    options: ["Strawberry", "Mango", "Kiwi", "Peach"],
    answer: 3,
  },
  {
    q: "Which Emperor's New Groove character has Her Highness canonically claimed?",
    options: ["Pacha", "Kuzco", "Yzma", "Kronk"],
    answer: 1,
  },
  {
    q: "Who authored the Sacred Text of 2019?",
    options: ["Nayiri", "Nelly", "Nada", "Khaled"],
    answer: 2,
  },
  {
    q: "What is Milo's permitted ration per meal?",
    options: [
      "Two scoops of large kibble",
      "One scoop of small kibble",
      "A handful, freely",
      "Whatever the Queen offers",
    ],
    answer: 1,
  },
  {
    q: "What must Koda do before any food may reach him?",
    options: ["Bark twice", "Roll over", "Sit and wait", "Shake"],
    answer: 2,
  },
  {
    q: "What is the proper form of shawarma?",
    options: [
      "Long, grilled until toasted",
      "Wrapped in pita",
      "Served on a plate",
      "In a bowl, deconstructed",
    ],
    answer: 0,
  },
  {
    q: "Which sauces are required upon a worthy shawarma?",
    options: [
      "Hummus and tahini",
      "Tzatziki and garlic",
      "Hot sauce alone",
      "Whatever the chef prefers",
    ],
    answer: 1,
  },
  {
    q: "Which burger ingredient is permanently forbidden?",
    options: ["Onion", "Pickle", "Bacon", "Mustard"],
    answer: 2,
  },
  {
    q: "Which burger establishment has been personally rejected by the Crown?",
    options: ["Five Guys", "In-N-Out", "Shake Shack", "The Habit"],
    answer: 1,
  },
  {
    q: "Where was Her Highness born?",
    options: ["Bahrain", "France", "Egypt", "United States"],
    answer: 2,
  },
  {
    q: "In which land was Her Highness raised?",
    options: ["Bahrain", "Egypt", "France", "Lebanon"],
    answer: 0,
  },
  {
    q: "In which country did Her Highness complete her legal training?",
    options: ["United States", "Egypt", "Bahrain", "France"],
    answer: 3,
  },
  {
    q: "Which corporation currently faces Her Highness in court?",
    options: ["Amazon", "Walmart", "McDonald's", "Starbucks"],
    answer: 2,
  },
  {
    q: "At what age did Her Highness begin the equestrian arts?",
    options: ["Four", "Six", "Ten", "Fourteen"],
    answer: 1,
  },
  {
    q: "How is the Queen's tea properly prepared?",
    options: [
      "Black, no additions",
      "Milk and two sugars",
      "A tiny bit of honey and fresh mint",
      "Lemon and sugar",
    ],
    answer: 2,
  },
  {
    q: "Beyond riding, in which equestrian sport does Her Highness compete?",
    options: ["Show jumping", "Dressage", "Polo", "Eventing"],
    answer: 2,
  },
];

const titleFor = (score: number) => {
  if (score === 20) return "Her Highness Herself";
  if (score >= 16) return "Worthy of Audience";
  if (score >= 11) return "Trusted Subject";
  if (score >= 6) return "Humble Servant";
  return "Peasant";
};

const verdictFor = (score: number) => {
  if (score === 20)
    return "A perfect score. Either you are the Queen, or you have been studying the sacred texts with frightening devotion.";
  if (score >= 16)
    return "You have proven your loyalty. An audience may be granted at the Queen's discretion.";
  if (score >= 11)
    return "Acceptable. You may continue your service, with study.";
  if (score >= 6)
    return "Your devotion is noted, but your knowledge requires significant work. Re-read the guide.";
  return "Disgraceful. Report at once to the rehabilitation centre. Bring the guide. Memorize it.";
};

export function Quiz() {
  const [started, setStarted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [answers, setAnswers] = useState<number[]>([]);
  const [done, setDone] = useState(false);

  const score = answers.filter((a, i) => a === questions[i]!.answer).length;

  const handleSelect = (idx: number) => {
    if (selected !== null) return;
    setSelected(idx);
  };

  const handleNext = () => {
    if (selected === null) return;
    const newAnswers = [...answers, selected];
    setAnswers(newAnswers);
    if (current === questions.length - 1) {
      setDone(true);
    } else {
      setCurrent(current + 1);
      setSelected(null);
    }
  };

  const handleRestart = () => {
    setStarted(false);
    setCurrent(0);
    setSelected(null);
    setAnswers([]);
    setDone(false);
  };

  const handleMidRestart = () => {
    setCurrent(0);
    setSelected(null);
    setAnswers([]);
    setDone(false);
  };

  if (!started) {
    return (
      <div className="max-w-xl w-full mx-auto text-center py-8">
        <p
          className="text-base leading-relaxed mb-10"
          style={{ color: "#4a3a2a" }}
        >
          Twenty questions concerning Her Highness. Answer with the care
          befitting your station. Your title shall be conferred upon completion.
        </p>
        <button
          onClick={() => setStarted(true)}
          className="px-10 py-3 text-sm tracking-[0.2em] uppercase border border-[#2b1d12] text-[#2b1d12] bg-transparent transition-colors hover:bg-[#2b1d12] hover:text-[#faf5ec]"
        >
          Begin
        </button>
      </div>
    );
  }

  if (done) {
    const title = titleFor(score);
    const verdict = verdictFor(score);
    return (
      <div className="max-w-xl w-full mx-auto text-center py-8">
        <div
          className="text-xs tracking-[0.3em] uppercase mb-8"
          style={{ color: "#8a6a3b" }}
        >
          Verdict
        </div>
        <div
          className="text-6xl sm:text-7xl mb-2"
          style={{ color: "#2b1d12", fontWeight: 300 }}
        >
          {score}
          <span style={{ color: "#8a6a3b" }} className="text-2xl sm:text-3xl"> / 20</span>
        </div>
        <div
          className="mx-auto w-16 h-px my-8"
          style={{ backgroundColor: "#8a6a3b" }}
        />
        <h3 className="text-3xl sm:text-4xl mb-6 italic text-pretty" style={{ color: "#2b1d12" }}>
          {title}
        </h3>
        <p
          className="text-base leading-relaxed mb-10 italic"
          style={{ color: "#4a3a2a" }}
        >
          {verdict}
        </p>
        <button
          onClick={handleRestart}
          className="px-10 py-3 text-sm tracking-[0.2em] uppercase border border-[#2b1d12] text-[#2b1d12] bg-transparent transition-colors hover:bg-[#2b1d12] hover:text-[#faf5ec]"
        >
          Begin Anew
        </button>
      </div>
    );
  }

  const question = questions[current]!;
  const isCorrect = selected === question.answer;

  return (
    <div className="max-w-xl w-full mx-auto py-8">
      <div className="flex items-center justify-between mb-12">
        <div
          className="text-xs tracking-[0.3em] uppercase"
          style={{ color: "#8a6a3b" }}
        >
          Question {String(current + 1).padStart(2, "0")}
        </div>
        <div
          className="text-xs tracking-[0.3em] uppercase"
          style={{ color: "#8a6a3b" }}
        >
          of {questions.length}
        </div>
      </div>

      <div className="w-full h-px mb-12" style={{ backgroundColor: "#e3d6bf" }}>
        <div
          className="h-px transition-all duration-500"
          style={{
            backgroundColor: "#8a6a3b",
            width: `${((current + 1) / questions.length) * 100}%`,
          }}
        />
      </div>

      <h3
        className="text-xl sm:text-2xl leading-snug mb-10 text-pretty"
        style={{ color: "#2b1d12" }}
      >
        {question.q}
      </h3>

      <div className="space-y-3 mb-10">
        {question.options.map((opt, idx) => {
          const isSelected = selected === idx;
          const isAnswer = idx === question.answer;
          const showResult = selected !== null;

          let borderColor = "#d6c5a8";
          let textColor = "#2b1d12";
          let bgColor = "transparent";

          if (showResult) {
            if (isAnswer) {
              borderColor = "#5a6e3a";
              textColor = "#3a4a26";
              bgColor = "#eef0e0";
            } else if (isSelected) {
              borderColor = "#8a3a3a";
              textColor = "#6a2a2a";
              bgColor = "#f0e0e0";
            } else {
              textColor = "#9a8a72";
            }
          }

          return (
            <button
              key={idx}
              onClick={() => handleSelect(idx)}
              disabled={showResult}
              className="w-full text-left px-6 py-4 border transition-colors text-base"
              style={{
                borderColor,
                color: textColor,
                backgroundColor: bgColor,
                cursor: showResult ? "default" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (!showResult) {
                  e.currentTarget.style.backgroundColor = "#f3eadb";
                }
              }}
              onMouseLeave={(e) => {
                if (!showResult) {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              <span
                className="inline-block w-6 mr-3 italic"
                style={{ color: "#8a6a3b" }}
              >
                {["i.", "ii.", "iii.", "iv."][idx]}
              </span>
              {opt}
            </button>
          );
        })}
      </div>

      {selected !== null && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div
            className="text-sm italic"
            style={{ color: isCorrect ? "#5a6e3a" : "#8a3a3a" }}
          >
            {isCorrect
              ? "Correct. The Queen approves."
              : "Incorrect. Reflect on your error."}
          </div>
          <div className="flex gap-3 w-full sm:w-auto">
            <button
              onClick={handleMidRestart}
              aria-label="Restart examination"
              title="Restart"
              className="p-3 border border-[#2b1d12] text-[#2b1d12] bg-transparent transition-colors hover:bg-[#2b1d12] hover:text-[#faf5ec]"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
                aria-hidden
              >
                <path d="M3 12a9 9 0 1 0 3-6.7" />
                <path d="M3 3v6h6" />
              </svg>
            </button>
            <button
              onClick={handleNext}
              className="flex-1 sm:flex-none px-8 py-3 sm:py-2 text-sm tracking-[0.2em] uppercase border border-[#2b1d12] text-[#2b1d12] bg-transparent transition-colors hover:bg-[#2b1d12] hover:text-[#faf5ec]"
            >
              {current === questions.length - 1 ? "Verdict" : "Continue"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
